import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useSessionTracking } from "@/hooks/useSessionTracking";
import { usePostMetrics } from "@/hooks/usePostMetrics";
import { useBlogPosts, type BlogPost } from "@/hooks/useBlogPosts";
import { useSharePost } from "@/hooks/useSharePost";
import { trackBlogView } from "@/hooks/useViewCount";
import { exportBlogPostsToTxt } from "@/lib/blogExport";

import BlogPostForm from "../BlogPostForm";
import { LoadingSpinner } from "../LoadingSpinner";
import { Button } from "../ui/button";
import BlogExportDialog from "./BlogExportDialog";
import BlogCardHeader from "./BlogCardHeader";
import BlogPostDetail from "./BlogPostDetail";
import BlogPostDialogs from "./BlogPostDialogs";
import BlogPostList from "./BlogPostList";
import { Dialog, DialogContent } from "../ui/dialog";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { useSubscriptions } from "@/hooks/useSubscriptions";
import AuthModal from "../AuthModal";
import WelcomeBanner from "../WelcomeBanner";
import { getSocialIcon } from "@/lib/socialIcons";

type CardState = "hidden" | "docked" | "fullscreen";

interface BlogCardProps {
  cardState: CardState;
  isMobile: boolean;
  layout?: "screen" | "tray" | "carousel";
  onCardStateChange?: (state: CardState) => void;
  onToggleCard?: () => void;
  onHideCard: () => void;
}

const BlogCard: React.FC<BlogCardProps> = ({
  cardState,
  isMobile,
  layout = "screen",
  onCardStateChange,
  onToggleCard,
  onHideCard,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, isReviewer, loading: authLoading } = useAuth();
  const isTrayLayout = layout === "tray";
  const isCarouselLayout = layout === "carousel";
  const { sessionKey } = useSessionTracking();
  const { sharePost } = useSharePost();
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [loadingPost, setLoadingPost] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<BlogPost | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { isSubscribed, toggleSubscription, loading: subscriptionsLoading } = useSubscriptions(user?.id);
  const [showPublishWarning, setShowPublishWarning] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [postToPublish, setPostToPublish] = useState<string | null>(null);
  const [showPublishDateDialog, setShowPublishDateDialog] = useState(false);
  const [postToRepublish, setPostToRepublish] = useState<BlogPost | null>(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isResisting, setIsResisting] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [hasSlideInPlayed, setHasSlideInPlayed] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const commentsRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef(0);
  const lastUserIdRef = useRef<string | null>(null);
  const [targetSection, setTargetSection] = useState<string | null>(null);

  const {
    blogPosts,
    loading,
    availableTags,
    selectedTagFilter,
    setSelectedTagFilter,
    sortOrder,
    setSortOrder,
    fetchBlogPosts,
  } = useBlogPosts({ authLoading, isAdmin, isReviewer });

  // Batch fetch metrics for all visible posts (list view only)
  const postIds = useMemo(() => blogPosts.map(p => p.id), [blogPosts]);
  const { getMetrics } = usePostMetrics(postIds);
  const publishedPosts = blogPosts.filter((post) => post.published);
  const unpublishedPosts = blogPosts.filter((post) => !post.published);

  useEffect(() => {
    if (isTrayLayout || isCarouselLayout) {
      setHasMounted(true);
      setHasSlideInPlayed(true);
      return;
    }

    // Delay blog card to appear after chat card completes
    const timer = setTimeout(() => {
      setHasMounted(true);
      setHasSlideInPlayed(true);
    }, 7000);
    return () => clearTimeout(timer);
  }, [isTrayLayout]);

  useEffect(() => {
    if (authLoading) return;

    if (lastUserIdRef.current === null && user?.id) {
      lastUserIdRef.current = user.id;
      return;
    }

    if (lastUserIdRef.current !== (user?.id ?? null)) {
      lastUserIdRef.current = user?.id ?? null;
      setSelectedPost(null);
      setLoadingPost(false);
      setShowForm(false);
      setEditingPost(null);
      setDeleteDialogOpen(false);
      setPostToDelete(null);
      setShowAuthModal(false);
      setShowPublishWarning(false);
      setPostToPublish(null);
      setShowPublishDateDialog(false);
      setPostToRepublish(null);
      setShowExportDialog(false);
      setIsResisting(false);
      scrollPositionRef.current = 0;
      navigate(window.location.pathname, { replace: true });
      contentRef.current?.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [authLoading, user?.id]);

  // Scroll to section helper - scrolls the content container with proper alignment
  const scrollToSection = useCallback((section: "poll" | "comments") => {
    const targetId = section === "poll" ? "poll-section" : "comments-section";
    const OFFSET_PX = 16; // Small offset so header isn't flush with top
    
    let attempts = 0;
    const maxAttempts = 25;
    
    const triggerHighlight = (element: HTMLElement) => {
      // Remove class first in case it's already there
      element.classList.remove("deep-link-highlight");
      // Force reflow to restart animation
      void element.offsetWidth;
      element.classList.add("deep-link-highlight");
      
      // Remove after animation completes
      setTimeout(() => {
        element.classList.remove("deep-link-highlight");
      }, 1600);
    };
    
    const tryScroll = () => {
      const targetElement = document.getElementById(targetId);
      const container = contentRef.current;
      
      if (targetElement && container) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();
        const scrollTop = targetRect.top - containerRect.top + container.scrollTop - OFFSET_PX;
        
        container.scrollTo({ top: Math.max(0, scrollTop), behavior: "smooth" });
        
        // Trigger highlight after scroll settles
        setTimeout(() => triggerHighlight(targetElement), 400);
        
        // Re-align after potential layout shifts (images loading, etc.)
        setTimeout(() => {
          const updatedRect = targetElement.getBoundingClientRect();
          const updatedScrollTop = updatedRect.top - containerRect.top + container.scrollTop - OFFSET_PX;
          if (Math.abs(updatedScrollTop - container.scrollTop) > 20) {
            container.scrollTo({ top: Math.max(0, updatedScrollTop), behavior: "smooth" });
          }
        }, 500);
        return;
      }
      
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(tryScroll, 200);
      }
    };
    
    // Initial delay to let content start rendering
    setTimeout(tryScroll, 100);
  }, []);

  // Effect to handle deep linking via URL query params - reacts to URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const postId = urlParams.get("post");
    const section = urlParams.get("section") as "poll" | "comments" | null;

    if (!postId || blogPosts.length === 0) return;

    const targetPost = blogPosts.find((p) => p.id === postId);
    if (!targetPost) return;

    // If we're already viewing this post, just scroll to section if specified
    if (selectedPost?.id === postId) {
      if (section) {
        scrollToSection(section);
      }
      return;
    }

    // Open the post and scroll to section
    handlePostClick(targetPost, section);
  }, [location.search, blogPosts, selectedPost?.id, scrollToSection]);

  const handleCreatePost = () => {
    setEditingPost(null);
    setShowForm(true);
  };

  const handleEditPost = (post: BlogPost) => {
    setEditingPost(post);
    setShowForm(true);
  };

  const handleFormSave = () => {
    setShowForm(false);
    setEditingPost(null);
    fetchBlogPosts();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingPost(null);
  };

  const handlePostClick = (post: BlogPost, section?: string | null) => {
    // Store current scroll position and immediately reset to top
    if (contentRef.current) {
      scrollPositionRef.current = contentRef.current.scrollTop;
      contentRef.current.scrollTo({ top: 0, behavior: "instant" });
    }

    setLoadingPost(true);
    setSelectedPost(post);
    setTargetSection(section || null);

    // Update URL with post ID (and section if provided) using React Router
    const sectionParam = section ? `&section=${section}` : '';
    navigate(`${window.location.pathname}?post=${post.id}${sectionParam}`, { replace: true });

    // After post renders, scroll to section if needed
    setTimeout(() => {
      if (section === "poll" || section === "comments") {
        scrollToSection(section);
      }
      setLoadingPost(false);
    }, 150);

    trackBlogView(post.id, user?.id, sessionKey).catch((error) => {
      console.error("Error tracking view:", error);
    });
  };

  const handleBackToList = () => {
    setSelectedPost(null);

    // Clear URL parameter using React Router (so useLocation updates)
    navigate(window.location.pathname, { replace: true });

    // Restore scroll position
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollTo({ top: scrollPositionRef.current, behavior: "smooth" });
      }
    }, 100);
  };

  const handleTogglePublish = async (post: BlogPost, e: React.MouseEvent) => {
    e.stopPropagation();

    // If publishing (currently unpublished)
    if (!post.published) {
      // Check if this is a republish (was previously published)
      if (post.published_at) {
        // Show republish dialog to ask about date update
        setPostToRepublish(post);
        setShowPublishDateDialog(true);
      } else {
        // First time publishing - show warning with subscriber count
        try {
          const { data: count, error } = await supabase.rpc("get_subscriber_count", {
            subscription_type_param: "new_post",
          });

          if (error) {
            console.error("Error fetching subscriber count:", error);
          }

          setSubscriberCount(count || 0);
          setPostToPublish(post.id);
          setShowPublishWarning(true);
        } catch (error) {
          console.error("Error checking subscribers:", error);
          toast.error("Failed to check subscriber count");
        }
      }
    } else {
      // Unpublishing doesn't need warning
      await performPublishToggle(post, false);
    }
  };

  const performPublishToggle = async (post: BlogPost, updatePublishDate: boolean = true) => {
    try {
      const updateData: any = { published: !post.published };

      // If publishing and should update the date
      if (!post.published && updatePublishDate) {
        updateData.published_at = new Date().toISOString();
      }

      const { error } = await supabase.from("blog_posts").update(updateData).eq("id", post.id);

      if (error) throw error;

      toast.success(`Post ${!post.published ? "published" : "unpublished"} successfully`);
      fetchBlogPosts();
    } catch (error: any) {
      console.error("Error toggling publish status:", error);
      toast.error(`Error ${!post.published ? "publishing" : "unpublishing"} post: ${error.message}`);
    }
  };

  const confirmPublish = async () => {
    if (postToPublish) {
      const post = blogPosts.find((p) => p.id === postToPublish);
      if (post) {
        await performPublishToggle(post, true);
      }
      setShowPublishWarning(false);
      setPostToPublish(null);
    }
  };

  const handleRepublishWithDate = async (updateDate: boolean) => {
    if (postToRepublish) {
      // If updating date, show subscriber warning first
      if (updateDate) {
        try {
          const { data: count, error } = await supabase.rpc("get_subscriber_count", {
            subscription_type_param: "new_post",
          });

          if (error) {
            console.error("Error fetching subscriber count:", error);
          }

          setSubscriberCount(count || 0);
          setPostToPublish(postToRepublish.id);
          setShowPublishDateDialog(false);
          setShowPublishWarning(true);
        } catch (error) {
          console.error("Error checking subscribers:", error);
          toast.error("Failed to check subscriber count");
        }
      } else {
        // Keep original date, just republish without notification
        await performPublishToggle(postToRepublish, false);
        setShowPublishDateDialog(false);
        setPostToRepublish(null);
      }
    }
  };

  const handleDeleteClick = (post: BlogPost, e: React.MouseEvent) => {
    e.stopPropagation();
    setPostToDelete(post);
    setDeleteDialogOpen(true);
  };

  const handleDeletePost = async () => {
    if (!postToDelete) return;

    try {
      const { error } = await supabase.from("blog_posts").delete().eq("id", postToDelete.id);

      if (error) throw error;

      toast.success("Post deleted successfully");
      setDeleteDialogOpen(false);
      setPostToDelete(null);

      // If we're viewing the deleted post, go back to list
      if (selectedPost?.id === postToDelete.id) {
        setSelectedPost(null);
      }

      fetchBlogPosts();
    } catch (error: any) {
      console.error("Error deleting post:", error);
      toast.error(`Error deleting post: ${error.message}`);
    }
  };

  const formatDate = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  };

  const handleExportPosts = () => {
    if (blogPosts.length === 0) {
      toast.error("No posts to export");
      return;
    }
    setShowExportDialog(true);
  };

  // Show the blog post form dialog
  if (showForm) {
    return (
      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <BlogPostForm post={editingPost || undefined} onSave={handleFormSave} onCancel={handleFormCancel} />
        </DialogContent>
      </Dialog>
    );
  }

  // Render the blog card based on its state
  if (cardState === "docked" && !isTrayLayout) {
    return (
      <div
        className="fixed bottom-0 left-4 right-4 h-8 bg-blog-white flex items-center justify-between cursor-pointer shadow-xl hover:bg-hover transition-colors z-30 border-2 border-b-0 border-blog-border rounded-t-lg px-4 animate-gentle-bounce"
        onClick={onToggleCard}
      >
        <span className="text-foreground font-medium text-sm">Blog</span>
        <div className="flex flex-col gap-0.5 absolute left-1/2 transform -translate-x-1/2">
          <div className="w-6 h-0.5 bg-foreground/60 rounded"></div>
          <div className="w-6 h-0.5 bg-foreground/60 rounded"></div>
          <div className="w-6 h-0.5 bg-foreground/60 rounded"></div>
        </div>
      </div>
    );
  }

  const baseClasses = isTrayLayout
    ? "bg-blog-white shadow-xl transition-all duration-300 ease-in-out overflow-hidden flex flex-col"
    : "bg-blog-white border-2 border-blog-border shadow-xl transition-all duration-300 ease-in-out overflow-hidden flex flex-col";

  let positionClasses = "";
  let sizeClasses = "";
  let roundingClasses = "rounded-lg";

  if (isMobile) {
    if (cardState === "fullscreen") {
      positionClasses = "top-0 left-0 right-0 bottom-0";
      sizeClasses = "h-full w-full rounded-none";
    }
  } else {
    if (cardState === "fullscreen") {
      positionClasses = "top-8 left-4 bottom-6 right-4";
      sizeClasses = "";
      roundingClasses = "rounded-lg";
    }
  }

  const blogContent = (
    <div className="h-full flex flex-col">
      {/* Header */}
      <BlogCardHeader
        selectedPost={selectedPost}
        isAdmin={isAdmin}
        hasUser={Boolean(user)}
        isSubscribed={isSubscribed}
        toggleSubscription={toggleSubscription}
        subscriptionsLoading={subscriptionsLoading}
        onShowAuthModal={() => setShowAuthModal(true)}
        onBackToList={handleBackToList}
        onEditPost={() => {
          if (selectedPost) {
            handleEditPost(selectedPost);
          }
        }}
        onSharePost={() => {
          if (selectedPost) {
            sharePost(selectedPost);
          }
        }}
        onCreatePost={handleCreatePost}
        onExportPosts={handleExportPosts}
        availableTags={availableTags}
        selectedTagFilter={selectedTagFilter}
        onTagFilterChange={(value) => {
          setSelectedTagFilter(value);
          contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        }}
        sortOrder={sortOrder}
        onSortOrderChange={setSortOrder}
        onHeaderClick={() => {
          console.log("Header clicked - starting resist animation");
          setIsResisting(true);

          setTimeout(() => {
            console.log("Animation complete - hiding card");
            setIsResisting(false);
          }, 500);

          setTimeout(() => {
            onHideCard();
          }, 550);
        }}
      />

      {/* Welcome Banner */}
      <WelcomeBanner />

      {/* Content */}
      <div ref={contentRef} className="flex-1 overflow-y-auto overflow-x-hidden relative">
        {/* Loading spinner overlay */}
        {loadingPost && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
            <LoadingSpinner size={96} />
          </div>
        )}

        {loading ? (
          <div className="py-10">
            <LoadingSpinner size={140} />
          </div>
        ) : !selectedPost ? (
          // Blog list view
          <div className="p-4 space-y-4 max-w-[725px] mx-auto">
            {/* Filter Results Summary */}
            {selectedTagFilter && (
              <div className="mb-2 text-sm text-muted-foreground">
                Showing {blogPosts.length} post{blogPosts.length !== 1 ? "s" : ""} with tag:
                <span className="font-medium text-foreground ml-1">
                  {availableTags.find((t) => t.slug === selectedTagFilter)?.name}
                </span>
              </div>
            )}

            {blogPosts.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <p>No blog posts yet.</p>
                {isAdmin && (
                  <Button onClick={handleCreatePost} className="mt-4">
                    Create your first post
                  </Button>
                )}
              </div>
            ) : (isAdmin || isReviewer) ? (
              <div className="space-y-6">
                <div className="space-y-3">
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Published</h2>
                  {publishedPosts.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No published posts.</p>
                  ) : (
                    <BlogPostList
                      posts={publishedPosts}
                      isAdmin={isAdmin}
                      onPostClick={handlePostClick}
                      onTogglePublish={handleTogglePublish}
                      onEditPost={handleEditPost}
                      onDeletePost={handleDeleteClick}
                      onTagClick={(slug) => {
                        setSelectedTagFilter(slug);
                        contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      getMetrics={getMetrics}
                      getSocialIcon={getSocialIcon}
                      formatDate={formatDate}
                    />
                  )}
                </div>
                <div className="space-y-3">
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Not published yet
                  </h2>
                  {unpublishedPosts.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No unpublished posts.</p>
                  ) : (
                    <BlogPostList
                      posts={unpublishedPosts}
                      isAdmin={isAdmin}
                      onPostClick={handlePostClick}
                      onTogglePublish={handleTogglePublish}
                      onEditPost={handleEditPost}
                      onDeletePost={handleDeleteClick}
                      onTagClick={(slug) => {
                        setSelectedTagFilter(slug);
                        contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      getMetrics={getMetrics}
                      getSocialIcon={getSocialIcon}
                      formatDate={formatDate}
                    />
                  )}
                </div>
              </div>
            ) : (
              <BlogPostList
                posts={blogPosts}
                isAdmin={isAdmin}
                onPostClick={handlePostClick}
                onTogglePublish={handleTogglePublish}
                onEditPost={handleEditPost}
                onDeletePost={handleDeleteClick}
                onTagClick={(slug) => {
                  setSelectedTagFilter(slug);
                  contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                }}
                getMetrics={getMetrics}
                getSocialIcon={getSocialIcon}
                formatDate={formatDate}
              />
            )}
          </div>
        ) : (
          <BlogPostDetail
            post={selectedPost}
            commentsRef={commentsRef}
            formatDate={formatDate}
            getSocialIcon={getSocialIcon}
            targetSection={targetSection}
            onScrollToSection={scrollToSection}
          />
        )}
      </div>

      <BlogPostDialogs
        deleteDialogOpen={deleteDialogOpen}
        setDeleteDialogOpen={setDeleteDialogOpen}
        postToDelete={postToDelete}
        setPostToDelete={(post) => setPostToDelete(post as BlogPost | null)}
        handleDeletePost={handleDeletePost}
        showPublishDateDialog={showPublishDateDialog}
        setShowPublishDateDialog={setShowPublishDateDialog}
        setPostToRepublish={(post) => setPostToRepublish(post as BlogPost | null)}
        handleRepublishWithDate={handleRepublishWithDate}
        showPublishWarning={showPublishWarning}
        setShowPublishWarning={setShowPublishWarning}
        setPostToPublish={setPostToPublish}
        subscriberCount={subscriberCount}
        confirmPublish={confirmPublish}
      />

      <BlogExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        posts={blogPosts}
        availableTags={availableTags}
        onExport={(postsToExport, options) => {
          exportBlogPostsToTxt(postsToExport, options);
          toast.success(`Exported ${postsToExport.length} post${postsToExport.length === 1 ? "" : "s"} to file`);
        }}
      />

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
        }}
      />
    </div>
  );

  if (isTrayLayout) {
    return <div className={`${baseClasses} h-full w-full rounded-t-lg`}>{blogContent}</div>;
  }

  if (isCarouselLayout) {
    return <div className={`${baseClasses} h-full w-full rounded-3xl`}>{blogContent}</div>;
  }

  return (
    <div
      className={`
        ${baseClasses} ${positionClasses} ${sizeClasses} ${roundingClasses} fixed z-40
        transform-gpu will-change-transform
        ${!hasSlideInPlayed && hasMounted ? "animate-slide-in-up-slow" : ""}
        ${!hasMounted ? "translate-y-full opacity-0" : ""}
        ${isResisting ? "animate-bounce-resist-vertical" : ""}
        flex flex-col
      `}
    >
      {blogContent}
    </div>
  );
};

export default BlogCard;
